<template>
  <o-modal :active="true" :canCancel="false" class="modal-sm">
    <div class="modal-card">
      <header class="modal-card-head">
        <h4 class="modal-card-title">
          {{ isEdit ? 'Update Vendor' : 'Add a New Vendor' }}
          <span class="line-height required-label is-size-5 is-italic is-pulled-right mr-0 has-text-weight-normal">
            * required
          </span>
        </h4>
      </header>
      <div class="modal-card-body is-visible p-0">
        <div class="column">
          <div class="columns">
            <div class="column">
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3">
                    Vendor Name <span class="has-text-danger">*</span>
                  </label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      v-model.trim="newVendor.name"
                      placeholder="Enter a vendor name (3 or more characters)"
                    />
                  </div>
                </div>
              </div>
              <div class="field multi-tags">
                <div class="field-label">
                  <label class="label is-size-3">Material Categories<span class="has-text-danger">*</span></label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <mf-multi-select
                     v-model="newVendor.category"
                      placeholder="Add categories to this vendor"
                      :options="allCategories"
                      :multiple="true"
                      :tags="true"
                      :close-on-select="false"
                      label="name"
                      track-by="_id"
                      :clear-on-select="false"
                      @update:modelValue="getSubCategories"
                    />
                  </div>
                </div>
              </div>
              <div class="field multi-tags">
                <div class="field-label">
                  <label class="label is-size-3">Sub-Categories</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <mf-multi-select
                      v-model="newVendor.subCategory"
                      placeholder="Add sub-categories to this vendor"
                      :options="allSubCategories"
                      :multiple="true"
                      :tags="true"
                      :close-on-select="false"
                      label="name"
                      track-by="_id"
                      type="grouped"
                      group-values="val"
                      group-label="key"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div class="column">
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3">Street Address</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      placeholder="Enter a location"
                      v-model.trim="newVendor.address.location"
                    />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3">City</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      placeholder="Enter a City"
                      v-model.trim="newVendor.address.city"
                    />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3">State/Province</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      placeholder="State"
                      v-model.trim="newVendor.address.state"
                    />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3">Zip/Postal Code</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      placeholder="ZIP"
                      v-model.trim="newVendor.address.zip"
                    />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3">Phone</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      type="tel"
                      @keydown="(e) => { if (e.keyCode === 69) e.preventDefault() }"
                      placeholder="Enter vendor Phone"
                      v-model.trim="newVendor.address.phone"
                    />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3">Point of Contact</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      placeholder="Enter a contact name"
                      v-model.trim="newVendor.address.contactName"
                    />
                  </div>
                </div>
              </div>
              <div class="field">
                <div class="field-label">
                  <label class="label is-size-3">Contact Email</label>
                </div>
                <div class="field-body">
                  <div class="control">
                    <input
                      class="input"
                      type="text"
                      placeholder="Enter a contact email"
                      v-model.trim="newVendor.address.contactEmail"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <footer class="modal-card-foot is-justify-content-flex-end is-align-items-center">
        <div class="column is-narrow">
          <div>
            <button class="button is-outlined" @click="cancel">Cancel</button>
            <button
              class="button has-background-black-bis"
              :disabled="saveDisabled"
              @click="saveVendor"
            >
              Save Vendor
            </button>
          </div>
        </div>
      </footer>

      <o-loading :full-page="true" :active="isLoading" :can-cancel="false" />
    </div>
  </o-modal>
</template>

<script>
import { reactive, toRefs, computed, onBeforeMount } from 'vue';
import { isEmpty, trim, keyBy, map, groupBy, filter, debounce } from 'lodash';
import { useToast } from 'vue-toastification';
import Vendors from '@/models/Vendors';
import Validation from '@/components/utils/Validations';

export default {
  name: 'VendorsForm',
  props: {
    active: {
      type: Boolean,
      default: false,
    },
    vendor: {
      type: Object,
      default: () => ({}),
    },
  },
  emits: ['close', 'refresh-table'],
  setup(props, { emit }) {
    const toast = useToast();

    const emptyAddress = () => ({
      location: '',
      street: '',
      city: '',
      state: '',
      zip: '',
      phone: '',
      contactName: '',
      contactEmail: '',
    });

    const data = reactive({
      isLoading: false,
      newVendor: {
        name: '',
        code: '',
        desc: '',
        address: emptyAddress(),
        category: [],
        subCategory: [],
      },
      allCategories: [],
      allSubCategories: [],
    });

    const isEdit = computed(() => !isEmpty(props.vendor));

    const saveDisabled = computed(
      () => isEmpty(data.newVendor.name) || isEmpty(data.newVendor.category),
    );

    const hasValidLength = (field, min) => trim(field).length < min;

    const validateVendor = (vendor) => {
      if (isEmpty(vendor.name) || hasValidLength(vendor.name, 3)) {
        toast.error('Vendor name cannot be empty and must be at least 3 characters');
        return false;
      }
      if (isEmpty(vendor.category)) {
        toast.error('Category cannot be empty');
        return false;
      }
      if (!isEmpty(vendor.address)) {
        const { address } = vendor;
        if (!isEmpty(address.phone) && !Validation.validatePhoneNumber(address.phone)) {
          toast.error('Valid range for phone number length is 1 - 24!');
          return false;
        }
        if (!isEmpty(address.contactEmail) && !Validation.validateEmail(address.contactEmail)) {
          toast.error('Please enter a valid Contact Email');
          return false;
        }
        if (!isEmpty(address.zip) && !Validation.validateZip(address.zip)) {
          toast.error('Valid range for zip length is 1 - 24!');
          return false;
        }
        if (!isEmpty(address.state) && hasValidLength(address.state, 2)) {
          toast.error('State must be at least 2 characters long');
          return false;
        }
        for (const field of ['location', 'street', 'city', 'contactName']) {
          if (!isEmpty(address[field]) && hasValidLength(address[field], 3)) {
            toast.error(`${field} must be at least 3 characters long`);
            return false;
          }
        }
      }
      return true;
    };

    const groupSubCategories = (subCategories) => {
      if (!subCategories.length) return subCategories;
      const grouped = groupBy(subCategories, 'parent.name');
      return map(grouped, (val, key) => ({ val, key }));
    };

    const getSubCategories = async () => {
      const categoryIds = map(data.newVendor.category, '_id');
      const result = await Vendors.getCategories({
        categoryId: categoryIds.filter(Boolean),
        isParent: false,
      });
      const flat = result.data;
      const indexedSubCategories = keyBy(flat, '_id');
      data.allSubCategories = groupSubCategories(flat);
      if (!isEmpty(data.newVendor.subCategory)) {
        data.newVendor.subCategory = filter(
          data.newVendor.subCategory,
          (sc) => Object.keys(indexedSubCategories).includes(sc._id),
        );
      }
    };

    const saveVendor = debounce(async () => {
      if (!validateVendor(data.newVendor)) return;
      data.isLoading = true;
      try {
        if (!data.newVendor._id) {
          const result = await Vendors.addVendor([data.newVendor]);
          if (!isEmpty(result.failedData)) {
            const msg = result.failedData[0]?._errorMsg || 'Please contact ManufactOn Support';
            toast.error(`Couldn't add vendor. ${msg}`);
            return;
          }
          toast.success('New vendor added');
        } else {
          await Vendors.updateVendor(data.newVendor);
          toast.success('Vendor updated');
        }
        emit('refresh-table');
        emit('close', false);
      } catch (e) {
        const msg = typeof e === 'string' ? e : e?.data?.message || e?.msg || 'Please contact ManufactOn Support';
        toast.error(`Couldn't save vendor. ${msg}`);
      } finally {
        data.isLoading = false;
      }
    }, 500);

    const cancel = () => {
      emit('close', false);
    };

    onBeforeMount(async () => {
      data.isLoading = true;
      try {
        data.allCategories = (await Vendors.getCategories({ limit: 500 })).data;

        if (!isEmpty(props.vendor)) {
          data.newVendor = { ...props.vendor };
          data.newVendor.address = isEmpty(props.vendor.address)
            ? emptyAddress()
            : { ...props.vendor.address[0] };
          await getSubCategories();
        }
      } finally {
        data.isLoading = false;
      }
    });

    return {
      ...toRefs(data),
      isEdit,
      saveDisabled,
      saveVendor,
      cancel,
      getSubCategories,
    };
  },
};
</script>

<style scoped>
::v-deep(.multiselect__tag-icon:after) {
  color: #fff;
}
::v-deep(.multiselect__tag-icon:hover) {
  background-color: #1a1a1a;
}
.modal-card-head {
  background-color: #fff !important;
  border-bottom: none !important;
}
.modal-card-foot {
  border-top: 1px solid #84888c;
  background-color: #fff !important;
}
</style>